import { createSignal, createResource, createMemo, batch } from "solid-js"
import { createStore } from "solid-js/store"
import type { Expert, ExpertFormState, ExpertSkillOption } from "./expert-types"
import { ServerConnection } from "@/context/server"
import { useTabs } from "@/context/tabs"
import { expertApiFetch, expertListRevision, fetchExperts, invalidateExperts } from "./expert-api"

/** Module-level signal: carries expert context from expert library → homepage.
 * Set before navigating, consumed once by NewSessionDraftController. */
const [pendingExpert, setPendingExpert] = createSignal<{ id: string; name: string; avatar: string } | null>(null)

/** 表单 skill option → 提交给后端的结构：去掉空字段、递归处理 children。
 *  注意保留 preset_questions（一级叶子 + 子技能），旧实现只挑白名单字段会把它丢掉。 */
function normalizeSkillOption(option: ExpertSkillOption): ExpertSkillOption {
  const cleanQuestions = (qs: string[] | undefined) =>
    (qs ?? []).map((q) => q.trim()).filter(Boolean)
  const children = (option.children ?? [])
    .filter((child) => child.id.trim() || child.label.trim() || child.prefix?.trim())
    .map((child) => {
      const childQs = cleanQuestions(child.preset_questions)
      return {
        id: child.id.trim(),
        label: child.label.trim(),
        icon: child.icon?.trim() || undefined,
        show_icon: child.show_icon,
        prefix: child.prefix?.trim() || undefined,
        ...(childQs.length > 0 ? { preset_questions: childQs } : {}),
      }
    })
  const qs = cleanQuestions(option.preset_questions)
  return {
    id: option.id.trim(),
    label: option.label.trim(),
    icon: option.icon?.trim() || undefined,
    show_icon: option.show_icon,
    prefix: option.prefix?.trim() || undefined,
    ...(children.length > 0 ? { children } : {}),
    ...(qs.length > 0 ? { preset_questions: qs } : {}),
  }
}

export function createExpertController(getServer: () => ServerConnection.Any | undefined = () => undefined) {
  const tabs = useTabs()
  const [searchQuery, setSearchQuery] = createSignal("")
  const [showForm, setShowForm] = createSignal(false)
  const [editingExpertId, setEditingExpertId] = createSignal<string | null>(null)
  const [selectedExpertId, setSelectedExpertId] = createSignal<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = createSignal<string | null>(null)
  const [loading, setLoading] = createSignal(false)
  const [error, setError] = createSignal<string | null>(null)

  const emptyForm: ExpertFormState = {
    name: "",
    alias: "",
    description: "",
    avatar: "",
    tags: "",
    skill_id: "",
    skill_prefix: "",
    skill_options: [],
    preset_questions: [""],
    show_on_home: true,
  }
  const [formData, setFormData] = createStore<ExpertFormState>({ ...emptyForm })

  const [apiExperts, { refetch: refetchExperts }] = createResource(
    () => [getServer()?.http.url, expertListRevision()] as const,
    async () => {
      try {
        const data = await fetchExperts(getServer)
        return data ?? []
      } catch {
        return []
      }
    },
  )

  const allExperts = createMemo<Expert[]>(() => apiExperts() ?? [])

  const filteredExperts = createMemo<Expert[]>(() => {
    const list = allExperts()
    const q = searchQuery().toLowerCase().trim()
    if (!q) return list
    return list.filter((e) => {
      const matchName = e.name.toLowerCase().includes(q)
      const matchAlias = (e.alias ?? "").toLowerCase().includes(q)
      const matchDesc = (e.description ?? "").toLowerCase().includes(q)
      const matchTags = (e.tags ?? []).some((t) => t.toLowerCase().includes(q))
      return matchName || matchAlias || matchDesc || matchTags
    })
  })

  const selectedExpert = createMemo<Expert | null>(() => {
    const id = selectedExpertId()
    if (!id) return null
    return allExperts().find((e) => e.id === id) ?? null
  })
  const editingExpert = createMemo<Expert | null>(() => {
    const id = editingExpertId()
    if (!id) return null
    return allExperts().find((expert) => expert.id === id) ?? null
  })

  function openCreate() {
    batch(() => {
      setFormData({ ...emptyForm })
      setEditingExpertId(null)
      setShowForm(true)
    })
  }

  /** avatar：卡片新头像（icon1-5 的资源地址）由列表位置决定，调用方解析后传进来，
   * 让编辑弹框的输入框和卡片显示的是同一个头像；不传则沿用专家原有的 avatar。 */
  function openEdit(expert: Expert, avatar?: string) {
    batch(() => {
      setFormData({
        name: expert.name,
        alias: expert.alias ?? "",
        description: expert.description ?? "",
        avatar: avatar ?? expert.avatar ?? "",
        tags: (expert.tags ?? []).join(", "),
        skill_id: expert.skill_id ?? "",
        skill_prefix: expert.skill_prefix ?? "",
        skill_options: (expert.skill_options ?? []).map((option) => ({
          ...option,
          children: (option.children ?? []).map((child) => ({ ...child })),
        })),
        preset_questions: expert.preset_questions?.length ? [...expert.preset_questions] : [""],
        show_on_home: expert.show_on_home,
      })
      setEditingExpertId(expert.id)
      setShowForm(true)
    })
  }

  function closeForm() {
    setShowForm(false)
    setEditingExpertId(null)
  }

  async function saveForm() {
    const name = formData.name.trim()
    if (!name) return

    setLoading(true)
    setError(null)
    try {
      const skillOptions = formData.skill_options
        .filter((option) => option.id.trim() || option.label.trim() || option.prefix?.trim())
        .map(normalizeSkillOption)
      // 只有第一级就是叶子技能时才回填 skill_id / skill_prefix（兼容旧的单技能专家）；
      // 「类别 → 子技能」型专家（如情报专家）没有单一默认技能，保持为空。
      const primary = formData.skill_options[0]
      const primaryIsLeaf = primary ? (primary.children?.length ?? 0) === 0 : false
      const payload = {
        name,
        alias: formData.alias.trim() || undefined,
        description: formData.description.trim() || undefined,
        avatar: formData.avatar.trim() || undefined,
        tags: formData.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        skill_id: primaryIsLeaf && primary ? primary.id.trim() || undefined : undefined,
        skill_prefix: primaryIsLeaf && primary ? primary.prefix?.trim() || undefined : undefined,
        skill_options: skillOptions,
        preset_questions: formData.preset_questions.map((q) => q.trim()).filter(Boolean),
        status: "published" as const,
        show_on_home: formData.show_on_home,
      }

      const eid = editingExpertId()
      if (eid) {
        // 内置 / 自定义专家统一走完整更新。内置专家同样可编辑，
        // 后端靠 seed_version 保证用户的修改不会被重启覆盖。
        await expertApiFetch(getServer, `/${eid}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        })
      } else {
        await expertApiFetch(getServer, "", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        })
      }
      await refetchExperts()
      invalidateExperts()
      closeForm()
    } catch (e) {
      setError(e instanceof Error ? e.message : "保存失败")
    } finally {
      setLoading(false)
    }
  }

  async function deleteExpert(id: string) {
    setLoading(true)
    setError(null)
    try {
      await expertApiFetch(getServer, `/${id}`, { method: "DELETE" })
      await refetchExperts()
      invalidateExperts()
    } catch (e) {
      setError(e instanceof Error ? e.message : "删除失败")
    } finally {
      setLoading(false)
    }
  }

  function previewExpert(expert: Expert) {
    setSelectedExpertId(expert.id)
  }

  async function startConversation(expert: Expert, question?: string) {
    const conn = getServer()
    if (!conn) return
    const serverKey = ServerConnection.key(conn)
    // Set expert context for homepage pill display (consumed once by NewSessionDraftController)
    setPendingExpert({ id: expert.id, name: expert.name, avatar: expert.avatar || "🤖" })
    // Combine skill prefix + question into a single prompt.
    // e.g. skill_prefix="/tang-poem " + question="帮我生成唐诗" → "/tang-poem 帮我生成唐诗"
    const skill = (expert.skill_prefix ?? "").trim()
    const fullPrompt = question ? `${skill}${skill && !skill.endsWith(" ") ? " " : ""}${question}` : skill || undefined
    // Create a new draft tab with the combined prompt.
    await tabs.newDraft({ server: serverKey, directory: "/" }, fullPrompt)
  }

  function addPresetQuestion() {
    setFormData("preset_questions", [...formData.preset_questions, ""])
  }

  function removePresetQuestion(index: number) {
    setFormData(
      "preset_questions",
      formData.preset_questions.filter((_, i) => i !== index),
    )
  }

  function setPresetQuestion(index: number, value: string) {
    setFormData("preset_questions", index, value)
  }

  function addSkillOption() {
    setFormData("skill_options", [
      ...formData.skill_options,
      { id: "", label: "", icon: "", show_icon: true, prefix: "", children: [], preset_questions: [] },
    ])
  }

  function removeSkillOption(index: number) {
    setFormData(
      "skill_options",
      formData.skill_options.filter((_, optionIndex) => optionIndex !== index),
    )
  }

  function setSkillOption(
    index: number,
    field: "id" | "label" | "icon" | "prefix" | "show_icon",
    value: string | boolean,
  ) {
    setFormData("skill_options", index, field, value)
  }

  /** 在某个一级技能下追加一个子技能（使该一级项成为「类别」） */
  function addChildSkillOption(index: number) {
    const children = formData.skill_options[index]?.children ?? []
    setFormData("skill_options", index, "children", [
      ...children,
      { id: "", label: "", icon: "", show_icon: true, prefix: "", preset_questions: [] },
    ])
  }

  function removeChildSkillOption(index: number, childIndex: number) {
    const children = formData.skill_options[index]?.children ?? []
    setFormData(
      "skill_options",
      index,
      "children",
      children.filter((_, i) => i !== childIndex),
    )
  }

  function setChildSkillOption(
    index: number,
    childIndex: number,
    field: "id" | "label" | "icon" | "prefix" | "show_icon",
    value: string | boolean,
  ) {
    const children = formData.skill_options[index]?.children ?? []
    setFormData(
      "skill_options",
      index,
      "children",
      children.map((child, i) => (i === childIndex ? ({ ...child, [field]: value } as ExpertSkillOption) : child)),
    )
  }

  // —— 叶子技能级预置问题（一级叶子 + 子技能），镜像专家级 add/remove/set ——
  // 一级叶子技能的预置问题
  function addSkillPresetQuestion(index: number) {
    const qs = formData.skill_options[index]?.preset_questions ?? []
    setFormData("skill_options", index, "preset_questions", [...qs, ""])
  }
  function removeSkillPresetQuestion(index: number, qIndex: number) {
    const qs = formData.skill_options[index]?.preset_questions ?? []
    setFormData(
      "skill_options",
      index,
      "preset_questions",
      qs.filter((_, i) => i !== qIndex),
    )
  }
  function setSkillPresetQuestion(index: number, qIndex: number, value: string) {
    setFormData("skill_options", index, "preset_questions", qIndex, value)
  }
  // 子技能的预置问题
  function addChildPresetQuestion(index: number, childIndex: number) {
    const children = formData.skill_options[index]?.children ?? []
    const qs = children[childIndex]?.preset_questions ?? []
    setFormData("skill_options", index, "children", childIndex, "preset_questions", [...qs, ""])
  }
  function removeChildPresetQuestion(index: number, childIndex: number, qIndex: number) {
    const children = formData.skill_options[index]?.children ?? []
    const qs = children[childIndex]?.preset_questions ?? []
    setFormData(
      "skill_options",
      index,
      "children",
      childIndex,
      "preset_questions",
      qs.filter((_, i) => i !== qIndex),
    )
  }
  function setChildPresetQuestion(index: number, childIndex: number, qIndex: number, value: string) {
    const children = formData.skill_options[index]?.children ?? []
    const qs = children[childIndex]?.preset_questions ?? []
    setFormData(
      "skill_options",
      index,
      "children",
      childIndex,
      "preset_questions",
      qs.map((q, i) => (i === qIndex ? value : q)),
    )
  }

  return {
    experts: filteredExperts,
    allExperts,
    selectedExpert,
    editingExpert,
    selectedExpertId,
    setSelectedExpertId,
    searchQuery,
    setSearchQuery,
    showForm,
    editingExpertId,
    formData,
    setFormData,
    confirmDeleteId,
    setConfirmDeleteId,
    loading,
    error,
    openCreate,
    openEdit,
    closeForm,
    saveForm,
    deleteExpert,
    previewExpert,
    startConversation,
    addPresetQuestion,
    removePresetQuestion,
    setPresetQuestion,
    addSkillOption,
    removeSkillOption,
    setSkillOption,
    addChildSkillOption,
    removeChildSkillOption,
    setChildSkillOption,
    addSkillPresetQuestion,
    removeSkillPresetQuestion,
    setSkillPresetQuestion,
    addChildPresetQuestion,
    removeChildPresetQuestion,
    setChildPresetQuestion,
  }
}

export type ExpertController = ReturnType<typeof createExpertController>

/** Exported for NewSessionDraftController to consume pending expert context */
export { pendingExpert, setPendingExpert }
